/** Maximum FIT payload accepted by the preview upload boundary. */
export const MAX_FIT_FILE_BYTES = 8 * 1024 * 1024;

const MINIMUM_FIT_BYTES = 14;
const MINIMUM_HEADER_BYTES = 12;
const MAXIMUM_HEADER_BYTES = 64;

/** Error raised when a payload cannot be a bounded FIT file. */
export class FitEnvelopeError extends Error {
  /**
   * Creates a safe, user-presentable FIT envelope error.
   *
   * @param message - Bounded explanation without payload content.
   */
  constructor(message: string) {
    super(message);
    this.name = "FitEnvelopeError";
  }
}

/**
 * Performs inexpensive structural checks before a FIT parser or object store
 * receives untrusted bytes.
 *
 * @param bytes - Candidate FIT file bytes.
 * @throws FitEnvelopeError when size, signature, header, or declared length is invalid.
 */
export function validateFitEnvelope(bytes: Uint8Array): void {
  if (bytes.byteLength < MINIMUM_FIT_BYTES) {
    throw new FitEnvelopeError("The FIT file is too short.");
  }
  if (bytes.byteLength > MAX_FIT_FILE_BYTES) {
    throw new FitEnvelopeError("The FIT file exceeds the 8 MB limit.");
  }

  const headerSize = bytes[0]!;
  if (headerSize < MINIMUM_HEADER_BYTES || headerSize > MAXIMUM_HEADER_BYTES) {
    throw new FitEnvelopeError("The FIT header size is invalid.");
  }
  if (headerSize > bytes.byteLength - 2) {
    throw new FitEnvelopeError("The FIT header extends beyond the file.");
  }

  const signature = String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!);
  if (signature !== ".FIT") {
    throw new FitEnvelopeError("The selected file does not contain a FIT signature.");
  }

  const declaredDataBytes = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    .getUint32(4, true);
  const declaredEnd = headerSize + declaredDataBytes;
  if (declaredEnd > bytes.byteLength) {
    throw new FitEnvelopeError("The FIT file is truncated.");
  }
}
